import { Box, Stack, Typography } from '@mui/material';
import { FC, ReactNode } from 'react';
import { ChronologyDirection, ChronologyStep, StepStatus } from '../types';
import { ChronologySignerItem } from './chronology-signer';
import { t } from 'i18next';

interface ChronologyItemProps {
  step: ChronologyStep;
  status: StepStatus;
  isLast: boolean;
  direction?: ChronologyDirection;
  columnWidth?: string;
}

interface StatusColors {
  iconColor: string;
  background: string;
  borderColor: string;
  labelColor: string;
  captionColor: string;
}

const STATUS_COLORS: Record<StepStatus, StatusColors> = {
  previous: {
    iconColor: '#47BF33',
    background: '#EBFFE8',
    borderColor: '#47BF33',
    labelColor: '#000000',
    captionColor: '#C4C4C4',
  },
  current: {
    iconColor: '#1555FC',
    background: '#E7EDFF',
    borderColor: '#1555FC',
    labelColor: '#000000',
    captionColor: '#1555FC',
  },
  future: {
    iconColor: '#FFFFFF',
    background: '#D9D9D9',
    borderColor: '#C4C4C4',
    labelColor: '#C4C4C4',
    captionColor: '#C4C4C4',
  },
};

const getCaption = (step: ChronologyStep, status: StepStatus) => {
  if (status === 'future') return t('chronology.waiting_prev_step');
  if (status === 'current')
    return step.currentCaption ?? t('chronology.waiting_prev_step');
  return step.caption ?? '';
};

export const ChronologyItem: FC<ChronologyItemProps> = ({
  step,
  status,
  isLast,
  direction = 'vertical',
  columnWidth,
}) => {
  const colors = STATUS_COLORS[status];
  const isHorizontal = direction === 'horizontal';
  const caption = step.rejectedCaption ?? getCaption(step, status);
  const captionColor = step.rejectedCaption
    ? 'error.main'
    : colors.captionColor;

  const circle: ReactNode = (
    <Box
      sx={{
        width: '31px',
        height: '31px',
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        background: colors.background,
        border: `1px solid ${colors.borderColor}`,
        color: colors.iconColor,
      }}
    >
      {step.icon}
    </Box>
  );

  const line: ReactNode = !isLast && (
    <Box
      sx={{
        flexGrow: 1,
        background: colors.borderColor,
        ...(isHorizontal
          ? { height: '1px', minWidth: '24px' }
          : { width: '1px', minHeight: '24px' }),
      }}
    />
  );

  const content: ReactNode = (
    <>
      <Typography
        sx={{ fontSize: '1.4rem', fontWeight: 500, color: colors.labelColor }}
      >
        {step.label}
      </Typography>
      <Typography
        sx={{ fontSize: '1.3rem', fontWeight: 500, color: captionColor }}
      >
        {caption}
      </Typography>

      {step.rejectionReason && (
        <Typography
          sx={{ fontSize: '1.3rem', fontWeight: 500, color: '#C4C4C4' }}
        >
          {step.rejectionReason}
        </Typography>
      )}

      {step.signers && (
        <Stack direction='column' gap={1} mt={1}>
          {step.signers.map((signer) => (
            <ChronologySignerItem key={signer.label} signer={signer} />
          ))}
        </Stack>
      )}
    </>
  );

  if (isHorizontal) {
    return (
      <Stack
        direction='column'
        sx={
          columnWidth
            ? { width: columnWidth, flexGrow: 0, flexShrink: 0 }
            : isLast
              ? { flexGrow: 0, flexShrink: 0 }
              : { flexGrow: 1, flexBasis: 0 }
        }
      >
        <Stack direction='row' alignItems='center'>
          {circle}
          {line}
        </Stack>

        <Stack
          direction='column'
          sx={{ pr: 2, pt: 1, minWidth: step.signers ? '260px' : 0 }}
        >
          {content}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction='row' alignItems='stretch' gap={1.5}>
      <Stack direction='column' alignItems='center' sx={{ flexShrink: 0 }}>
        {circle}
        {line}
      </Stack>

      <Stack direction='column' flexGrow={1} pb={isLast ? 0 : 2.5}>
        {content}
      </Stack>
    </Stack>
  );
};
